import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { SettingsState } from '../../organisms/SelectSettings';
import { Paper, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, TextField } from '@material-ui/core';
import {
  GroupListType,
  PH_CUR_GROUP_SET,
  PhaseCurrentSelectItem,
  GetPhaseCurrentGroupList,
  PutPhaseCurrentGroupList,
} from '../../../function/GroupDataModel';
import ItemComboBox, { ComboBoxItemType } from '../../atoms/ComboBox/ItemComboBox';
import SaveCancel from './SaveCancel';
import { useModal } from '../../../function/useModal';
import ConfirmDialog, { ConfirmDialogResponse } from '../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../atoms/Dialog/MessageDialog';
import { useTranslation } from 'react-i18next';
import CreateGroupDialog, { CreateGroupDialogResponse } from './CreateGroupDialog';

type PhaseCurrentSettingProps = {
  editData: SettingsState;
};

type SaveParameter = {
  list: PH_CUR_GROUP_SET[];
};

const MaxGroup: number = 128;

const useStyles = makeStyles(
  createStyles({
    tableStyle: {
      width: '400px',
      borderStyle: 'none',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
    },
    rowStyle: {
      borderStyle: 'none',
    },
    cellStyle: {
      borderStyle: 'none',
    },
    cellName: {
      width: '140px',
    },
    cellData: {
      width: '220px',
    },
    selectNo: {
      width: '120px',
    },
    selectName: {
      width: '200px',
    },
    dropdownStyle: {
      maxHeight: 300,
    },
    gridStyle: {
      borderStyle: 'solid',
      maxWidth: '500px',
    },
    tableItem: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      orderBottomStyle: 'inset',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      textAlign: 'center',
      minWidth: '80px',
      height: '8px',
      padding: '1px',
    },
    fixedCell: {
      backgroundColor: '#eeeeee',
    },
  })
);

const PhaseCurrentSetting: React.FC<PhaseCurrentSettingProps> = (props: PhaseCurrentSettingProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [groupNo, setGroupNo] = useState<number>(0);
  const [groupName, setGroupName] = useState<string>('');
  const [editList, setEditList] = useState<PH_CUR_GROUP_SET[]>([]);
  const [selectList, setSelectList] = useState<PhaseCurrentSelectItem[]>();
  const [devIdx, setDevIdx] = useState<number>(0);
  const [groupCombos, setGroupCombos] = useState<ComboBoxItemType[]>();
  const [selectCombos, setSelectCombos] = useState<ComboBoxItemType[]>();
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [kindR, setKindR] = useState<string>('');
  const [kindS, setKindS] = useState<string>('');
  const [kindT, setKindT] = useState<string>('');
  const [itemR, setItemR] = useState<string>('');
  const [itemS, setItemS] = useState<string>('');
  const [itemT, setItemT] = useState<string>('');
  const [flgCrtDlg, actCrtDlg] = useModal<CreateGroupDialogResponse>();

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('PhaseCurrentSetting.はい', 'はい')}
      cancel={t('PhaseCurrentSetting.いいえ', 'いいえ')}
    />
  ) : null;
  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;
  const crtdlg = flgCrtDlg ? (
    <CreateGroupDialog dialog={actCrtDlg} exists={groupCombos?.map<number>((gc) => gc.index)} maxGroup={MaxGroup} />
  ) : null;

  console.log('-- CurrentSetting --');

  useEffect(() => {
    console.log('-- fetch current settings --');
    GetPhaseCurrentGroupList().then((result: GroupListType) => {
      const groupList: PH_CUR_GROUP_SET[] = result.PH_CUR_GROUP_SETs.filter((ph) => ph.GROUP_NAME !== '');
      setEditList(groupList);
      setSelectList(result.PhCurSelectItems);

      const selects = result.PhCurSelectItems.map<ComboBoxItemType>((sel) => {
        return { index: sel.DEV_INDEX, name: sel.GROUP_NAME };
      });
      selects.unshift({ index: 0, name: '---' });
      setSelectCombos(selects);

      if (groupList.length > 0) {
        setGroupNo(groupList[0].GROUP_NO);
        setGroupName(groupList[0].GROUP_NAME);
        setDevIdx(groupList[0].DEV_INDEX);
        var sel = result.PhCurSelectItems.find((si) => si.DEV_INDEX == groupList[0].DEV_INDEX);
        setKindR(sel?.KIND_R ?? '');
        setKindS(sel?.KIND_S ?? '');
        setKindT(sel?.KIND_T ?? '');
        setItemR(sel?.ITEM_R ?? '');
        setItemS(sel?.ITEM_S ?? '');
        setItemT(sel?.ITEM_T ?? '');
      }

      props.editData.SaveFunction = saveCurrentSetting;
      props.editData.IsUpdate = false;
    });
  }, [props.editData, props.editData.CancelCondition]);

  useEffect(() => {
    const groups = editList.map<ComboBoxItemType>((grp) => {
      return { index: grp.GROUP_NO, name: grp.GROUP_NO.toString() };
    });
    setGroupCombos(groups);

    if (!editList.find((el) => el.GROUP_NO == groupNo)) {
      if (editList.length > 0) {
        const grp = editList[0];
        setGroupNo(grp.GROUP_NO);
      }
    }
  }, [editList]);

  useEffect(() => {
    const dev = editList?.find((ed) => ed.GROUP_NO === groupNo);
    setGroupName(dev?.GROUP_NAME ?? '');
    setDevIdx(dev?.DEV_INDEX ?? 0);
  }, [groupNo]);

  useEffect(() => {
    var sel = selectList?.find((si) => si.DEV_INDEX == devIdx);
    setKindR(sel?.KIND_R ?? '');
    setKindS(sel?.KIND_S ?? '');
    setKindT(sel?.KIND_T ?? '');
    setItemR(sel?.ITEM_R ?? '');
    setItemS(sel?.ITEM_S ?? '');
    setItemT(sel?.ITEM_T ?? '');
  }, [devIdx]);

  const groupNameChange = (name: string) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(name);
      grp.GROUP_NAME = name;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;
    }
  };
  const targetChange = (idx: number) => {
    const sel = selectList?.find((ss) => ss.DEV_INDEX === idx);
    const edt = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (edt) {
      if (sel) {
        edt.DEV_INDEX = sel.DEV_INDEX;
        // edt.GROUP_NAME = sel.GROUP_NAME;
        edt.PLC_NO = sel.PLC_NO;
        edt.SET_NO_R = sel.SET_NO_R;
        edt.SET_NO_S = sel.SET_NO_S;
        edt.SET_NO_T = sel.SET_NO_T;
        edt.DSP_FLG = 1;
      } else {
        // 先頭を選択
        edt.DEV_INDEX = 0;
        // edt.GROUP_NAME = '';
        edt.PLC_NO = 0;
        edt.SET_NO_R = 0;
        edt.SET_NO_S = 0;
        edt.SET_NO_T = 0;
        edt.DSP_FLG = 0;
      }
      setEditList(editList.concat());
      setDevIdx(edt.DEV_INDEX);
      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;
    }
  };
  const saveCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        const errmsg = validateSetting(sp.list);
        if (errmsg === '') {
          PutPhaseCurrentGroupList(sp.list)
            .then((count: number) => {
              console.log(count);

              resolve('done');
            })
            .catch((reasone) => reject(reasone));
        } else {
          setDlgmsg(errmsg);
          actMsgDlg.open().apply((_) => setDlgmsg(''));
          reject('not save');
        }
      } else {
        reject('data nothing');
      }
    });
  };
  const validateSetting = (groups: PH_CUR_GROUP_SET[]): string => {
    for (var idx = 0; idx < groups.length; idx++) {
      if (groups[idx].GROUP_NAME == '') {
        return t('PhaseCurrentSetting.グループ名必須', 'グループ名は必須です');
      }
    }
    return '';
  };
  const deleteCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        PutPhaseCurrentGroupList(sp.list)
          .then((count: number) => {
            console.log(count);

            resolve('done');
          })
          .catch((reasone) => reject(reasone));
      } else {
        reject('data nothing');
      }
    });
  };

  return (
    <div>
      <TableContainer>
        <Table className={classes.tableStyle}>
          <TableBody>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('PhaseCurrentSetting.グループNo', 'グループNo')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <ItemComboBox
                  itemList={groupCombos}
                  label=""
                  value={groupNo}
                  onChangeEvent={(e) => {
                    if (typeof e.target.value === 'number') {
                      setGroupNo(e.target.value);
                    } else if (typeof e.target.value === 'string') {
                      setGroupNo(parseInt(e.target.value));
                    }
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('PhaseCurrentSetting.グループ名称', 'グループ名称')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <TextField
                  value={groupName}
                  inputProps={{maxLength:32}}
                  onChange={(e) => {
                    groupNameChange(e.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
            {/* <TableRow>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>{t('PhaseCurrentSetting.グループ名称','グループ名称')}</TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <ItemComboBox
                  itemList={selectCombos}
                  label=""
                  value={devIdx}
                  onChangeEvent={(e) => {
                    if (typeof e.target.value === 'number') {
                      targetChange(e.target.value);
                    } else if (typeof e.target.value === 'string') {
                      targetChange(parseInt(e.target.value));
                    }
                  }}
                />
              </TableCell>
            </TableRow> */}
          </TableBody>
        </Table>
        <Table className={classes.gridStyle}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} width={'180px'}>
                {t('RawWaveformsComparisonSetting.機器番号', '機器番号')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('RawWaveformsComparisonSetting.計測種別', '計測種別')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('RawWaveformsComparisonSetting.項目', '項目')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DeviceSelectSub
              selectCombos={selectCombos}
              devIdx={devIdx}
              targetChange={targetChange}
              kind={kindR}
              item={itemR}
            />
            <DeviceSelectSub
              selectCombos={selectCombos}
              devIdx={devIdx}
              targetChange={targetChange}
              kind={kindS}
              item={itemS}
            />
            <DeviceSelectSub
              selectCombos={selectCombos}
              devIdx={devIdx}
              targetChange={targetChange}
              kind={kindT}
              item={itemT}
            />
          </TableBody>
        </Table>
        <SaveCancel
          saveAction={() => {
            setDlgmsg(t('PhaseCurrentSetting.保存しますか?', '保存しますか?'));
            actCfmDlg
              .open()
              .apply((_) => {
                saveCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsUpdate = false;
                  // props.editData.CloseSetting();
                });
              })
              .cancel(() => {});
          }}
          cancelAction={() => {
            if (props.editData.IsUpdate) {
              setDlgmsg(
                t(
                  'PhaseCurrentSetting.保存していないデータを破棄します。\nよろしいですか?',
                  '保存していないデータを破棄します。\nよろしいですか?'
                )
              );
              actCfmDlg
                .open()
                .apply((_) => {
                  //props.editData.IsUpdate = false;
                  props.editData.UpdateCancel();
                  // props.editData.CloseSetting();
                })
                .cancel(() => {});
            } else {
              // props.editData.CloseSetting();
            }
          }}
          createAction={() => {
            actCrtDlg.open().apply((info) => {
              const grp = editList.find((ed) => ed.GROUP_NO == info.no);
              if (!grp) {
                const newGrp: PH_CUR_GROUP_SET = {
                  GROUP_NO: info.no,
                  GROUP_NAME: '',
                  DSP_FLG: 0,
                  DEV_INDEX: 0,
                  PLC_NO: 0,
                  SET_NO_R: 0,
                  SET_NO_S: 0,
                  SET_NO_T: 0,
                };
                const newList = editList.concat(newGrp).sort((g1, g2) => g1.GROUP_NO - g2.GROUP_NO);
                setEditList(newList);
              }
              setGroupNo(info.no);
            });
          }}
          deleteAction={() => {
            setDlgmsg(t('PhaseCurrentSetting.削除しますか?', '削除しますか?'));
            actCfmDlg.open().apply((_) => {
              const grp = editList.find((ed) => ed.GROUP_NO == groupNo);
              if (grp) {
                grp.GROUP_NAME = '';
                deleteCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsSaveClose = true;
                  props.editData.IsUpdate = false;
                  setEditList(editList.filter((ph) => ph.GROUP_NAME !== ''));
                });
              }
            });
          }}
        />
      </TableContainer>
      {cfmdlg}
      {msgdlg}
      {crtdlg}
    </div>
  );
};

type DeviceSelectSubProps = {
  selectCombos: ComboBoxItemType[] | undefined;
  devIdx: number;
  targetChange: (idx: number) => void;
  kind: string;
  item: string;
};

const DeviceSelectSub: React.FC<DeviceSelectSubProps> = (props) => {
  const classes = useStyles();

  return (
    <TableRow>
      <TableCell className={classes.tableItem}>
        <ItemComboBox
          itemList={props.selectCombos}
          label=""
          value={props.devIdx}
          onChangeEvent={(e) => {
            if (typeof e.target.value === 'number') {
              props.targetChange(e.target.value);
            } else if (typeof e.target.value === 'string') {
              props.targetChange(parseInt(e.target.value));
            }
          }}
        />
      </TableCell>
      <TableCell className={classes.tableItem}>
        <div>{props.kind}</div>
      </TableCell>
      <TableCell className={classes.tableItem}>
        <div>{props.item}</div>
      </TableCell>
    </TableRow>
  );
};

export default PhaseCurrentSetting;
